import { supabase } from '../supabase';

export interface UploadResult {
  path: string;
  url: string;
}

export const storageApi = {
  // Upload image to Supabase Storage
  async uploadImage(file: File, bucket: string = 'images', folder?: string): Promise<{ data: UploadResult | null; error: any }> {
    try {
      console.log('Uploading image:', file.name, 'to bucket:', bucket, 'folder:', folder);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage upload error:', error);
        return { data: null, error };
      }

      console.log('Upload successful:', data.path);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      console.log('Public URL generated:', urlData.publicUrl);

      return {
        data: {
          path: data.path,
          url: urlData.publicUrl
        },
        error: null
      };
    } catch (error) {
      console.error('Storage upload exception:', error);
      return { data: null, error };
    }
  },

  // Delete image from Supabase Storage
  async deleteImage(path: string, bucket: string = 'images') {
    try {
      console.log('Deleting image:', path, 'from bucket:', bucket);
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) {
        console.error('Storage delete error:', error);
      } else {
        console.log('Delete successful:', data);
      }

      return { data, error };
    } catch (error) {
      console.error('Storage delete exception:', error);
      return { data: null, error };
    }
  },

  // Get public URL for image
  getPublicUrl(path: string, bucket: string = 'images') {
    try {
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);

      console.log('Generated public URL for path:', path, 'URL:', data.publicUrl);
      return data.publicUrl;
    } catch (error) {
      console.error('Error generating public URL:', error);
      return '';
    }
  },

  // List images in folder
  async listImages(folder?: string, bucket: string = 'images') {
    try {
      console.log('Listing images in bucket:', bucket, 'folder:', folder);
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(folder, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('Storage list error:', error);
      } else {
        console.log('List successful, found', data?.length || 0, 'items');
      }

      return { data, error };
    } catch (error) {
      console.error('Storage list exception:', error);
      return { data: null, error };
    }
  },

  // Check if bucket exists and is accessible
  async checkBucketAccess(bucket: string = 'images') {
    try {
      console.log('Checking bucket access for:', bucket);
      
      const { error } = await supabase.storage
        .from(bucket)
        .list('', { limit: 1 });

      if (error) {
        console.error('Bucket access check failed:', error);
        return { accessible: false, error };
      }

      console.log('Bucket is accessible');
      return { accessible: true, error: null };
    } catch (error) {
      console.error('Bucket access check exception:', error);
      return { accessible: false, error };
    }
  }
};